import axios from 'axios';
import type { CancelTokenSource } from 'axios';

import type { MfaStatus } from 'models/mfa';

import type { ServerError } from '../../models';
import type {
    GetMfaSetupLinkResponse,
    GetMfaStatusResponse,
    MfaVerificationRequest,
    MfaVerificationResponse,
} from '../../models/reqres';
import { exchangeApi, handleErrorCallback, noop } from '../core';

export function getMfaStatus(
    userId: string,
    onSuc: (method: MfaStatus) => void,
    onErr: (err: ServerError) => void,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .get<GetMfaStatusResponse>(`/users/${userId}/auth_configuration`, {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            onSuc(response.data.mfa_method);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(onFinally);

    return cancelRef;
}

export function getMfaSetupLink(
    userId: string,
    onSuc: (link: string) => void,
    onErr: (err: ServerError) => void,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .get<GetMfaSetupLinkResponse>(`/users/${userId}/auth_configuration/authenticator_app`, {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            onSuc(response.data.otp_url);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(onFinally);

    return cancelRef;
}

export function configureMfa(
    userId: string,
    code: string,
    mfaMethod: MfaStatus,
    onSuc: () => void,
    onErr: (err: ServerError) => void,
    onFinally: () => void = noop,
    sardineSessionKey?: string,
    // update-mfa gate code; required to enroll an authenticator app when the
    // server enforces the enrollment gate
    mfaCode?: string,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .put(
            `/users/${userId}/auth_configuration?four_digit_code=true`,
            {
                code: code,
                mfa_method: mfaMethod,
                sardine_session_key: sardineSessionKey || undefined,
                mfa_code: mfaCode || undefined,
            },
            { cancelToken: cancelRef.token },
        )
        .then(() => {
            onSuc();
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(onFinally);

    return cancelRef;
}

export function mfaVerification(
    userId: string,
    payload: MfaVerificationRequest,
    onSuc: (resp: MfaVerificationResponse) => void,
    onErr: (err: ServerError) => void,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();
    exchangeApi
        .post<MfaVerificationResponse>(`/users/${userId}/action_verification_mfa?four_digit_code=true`, payload, {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            onSuc(response.data);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}
